import { Person, PersonOff } from '@mui/icons-material';
import {
    Checkbox,
    Chip,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Stack,
    Typography,
    useTheme,
} from '@mui/material';
import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { PersonReadDTO } from '../api/dto';
import { useDeviceType } from '../hooks/useDeviceType';
import { PersonTooltip } from './PersonTooltip';

interface PersonItemMetadata {
    light?: boolean;
    striked?: boolean;
}

interface SelectablePersonListProps {
    persons: PersonReadDTO[];
    personsMetadata?: Record<number, PersonItemMetadata>;
    selection: Set<number>;
    updateSelection: (newSelection: Set<number>) => void;
    disabled?: boolean;
}

export const SelectablePersonList: React.FC<SelectablePersonListProps> = ({
    persons,
    personsMetadata = {},
    selection,
    updateSelection,
    disabled,
}) => {
    const theme = useTheme();
    const intl = useIntl();
    const { isMobile } = useDeviceType();

    const toggleSelectedAssignee = useCallback(
        (person: Pick<PersonReadDTO, 'id'>) =>
            updateSelection(
                new Set(
                    selection.has(person.id)
                        ? [...selection.values()].filter((id) => id !== person.id)
                        : [...selection.values(), person.id]
                )
            ),
        [selection, updateSelection]
    );
    const getBorders = (index: number) => {
        const borderRadius = 4;
        const selected = selection.has(persons[index].id);
        const previousSelected = index > 0 && selection.has(persons[index - 1].id);
        const nextSelected = index + 1 < persons.length && selection.has(persons[index + 1].id);
        const top = !(selected && previousSelected)
            ? { borderTopLeftRadius: borderRadius, borderTopRightRadius: borderRadius }
            : {};
        const bottom = !(selected && nextSelected)
            ? { borderBottomLeftRadius: borderRadius, borderBottomRightRadius: borderRadius }
            : {};
        return { ...top, ...bottom };
    };

    return (
        <List dense disablePadding>
            {persons.map((person, index) => {
                const meta = personsMetadata[person.id] ?? {};
                const PersonIcon = !meta.striked ? Person : PersonOff;
                const opacity = meta.light ? 0.4 : undefined;
                return (
                    <ListItem key={person.id} disablePadding>
                        <ListItemButton
                            selected={selection.has(person.id)}
                            onClick={() => toggleSelectedAssignee(person)}
                            disabled={disabled}
                            sx={{ ...getBorders(index) }}
                        >
                            {!isMobile && (
                                <ListItemIcon sx={{ opacity }}>
                                    <PersonTooltip person={person}>
                                        <PersonIcon color="action" sx={{ cursor: 'help' }} />
                                    </PersonTooltip>
                                </ListItemIcon>
                            )}
                            <ListItemText
                                primary={
                                    <Stack
                                        direction="row"
                                        spacing={1}
                                        alignItems="center"
                                        sx={{
                                            minHeight: 24 /* Height of the chip */,
                                            opacity,
                                        }}
                                    >
                                        <Typography variant="body2">{person.searchLabel}</Typography>
                                        {!!person.orgUnit && !isMobile && (
                                            <Chip size="small" label={person.orgUnit.code} />
                                        )}
                                    </Stack>
                                }
                                sx={{ my: 0 }}
                            />
                            {!isMobile && (
                                <ListItemIcon>
                                    <Checkbox
                                        checked={selection.has(person.id)}
                                        disableRipple
                                        sx={{
                                            py: 0,
                                            display: selection.size === 0 ? 'none' : undefined,
                                        }}
                                    />
                                </ListItemIcon>
                            )}
                        </ListItemButton>
                    </ListItem>
                );
            })}
        </List>
    );
};
